import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ModalConfirm } from '@repo/ui'
import { cn } from '@repo/utils'

import { getImageURL, IMAGE_SIZES } from 'images'
import { ImageCrop } from 'ui'
import { useMutate } from 'utils'

import { authSetAvatar } from 'actions/AuthActions'

import type { FC } from 'react'

interface AvatarResponse {
  data: Array<{
    id: string
    alias: string
    avatar: {
      id: string
      filemime: string
      filename: string
      processed_at?: number
    }
    avatar_id: string
    city_id: string
    digest_email_time: string
    first_name: string
    gender: string
    last_name: string
    picture: {
      id: string
      filemime: string
      filename: string
      processed_at: number
    }
    picture_id: string
    user_id: string
  }>
}

interface AvatarEditProps {
  className?: string
}

const AvatarEdit: FC<AvatarEditProps> = ({ className }) => {
  const dispatch = useDispatch()
  const image = useSelector((state: { auth: { usr: { avatar: string } } }) => state.auth.usr.avatar)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const imageURL = image ? getImageURL(image, IMAGE_SIZES.FIT_100_100) : undefined

  const imageSave = useMutate<FormData, AvatarResponse>(
    {
      formEncode: true,
      method: 'post',
      url: 'user/avatar',
    },
    { invalidate: ['settings'] },
  )

  const imageDelete = useMutate(
    {
      method: 'delete',
      url: 'user/avatar',
    },
    { invalidate: ['settings'] },
  )

  const onImageSave = async (_: string, blob: Blob) => {
    const formData = new FormData()
    formData.append('avatar', blob)
    const response = await imageSave.mutateAsync(formData)
    if (response?.data) {
      dispatch(authSetAvatar(response?.data?.[0]?.avatar?.filename))
    }
  }

  const onImageDelete = async () => {
    setConfirmDelete(false)
    await imageDelete.mutateAsync()
    dispatch(authSetAvatar(null))
  }

  return (
    <div className={cn('AvatarEdit flex flex-col gap-1', className)}>
      <div className="font-bold">Imagen de perfil</div>
      <ImageCrop
        allowPaste
        className="w-48"
        contentClassName="border border-dashed"
        cropHeight={200}
        cropWidth={200}
        onCrop={onImageSave}
        onRemove={() => setConfirmDelete(true)}
        readOnly={imageSave.isPending || imageDelete.isPending}
        src={imageURL}
      >
        <div>
          <svg className="size-12 text-grey" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8 0-1.168.258-2.275.709-3.276.154.09.308.182.456.276.396.25.791.5 1.286.688.494.187 1.088.312 1.879.312.792 0 1.386-.125 1.881-.313s.891-.437 1.287-.687.792-.5 1.287-.688c.494-.187 1.088-.312 1.88-.312s1.386.125 1.88.313c.495.187.891.437 1.287.687s.792.5 1.287.688c.178.067.374.122.581.171.191.682.3 1.398.3 2.141 0 4.411-3.589 8-8 8z" />
            <circle cx="8.5" cy="12.5" r="1.5" />
            <circle cx="15.5" cy="12.5" r="1.5" />
          </svg>
          <br />
          Agrega una imagen haciendo clic aquí
        </div>
      </ImageCrop>
      <ModalConfirm
        accent="red"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onImageDelete}
        open={confirmDelete}
        title="Eliminar la image de perfil del usuario"
      >
        ¿Confirmas el borrado de la imagen de perfil?
        <div className="p-2 text-sm">Esta acción no se puede revertir.</div>
      </ModalConfirm>
    </div>
  )
}

export default AvatarEdit
